import React, { useEffect, useState, useMemo } from "react";
import Papa from "papaparse";
import { AgGridReact } from "ag-grid-react";
import { ColDef, CellClassParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";

interface CSVViewerProps {
  url: string | null;
  visible?: boolean;
  fileName?: string;
}

const CSVViewer: React.FC<CSVViewerProps> = ({ url, visible = true, fileName }) => {
  const [data, setData] = useState<string[][]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCell, setSelectedCell] = useState<{
    row: number;
    col: number;
  } | null>(null);

  useEffect(() => {
    if (!url || !visible) return;
    setLoading(true);
    setError(null);
    setData([]);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch CSV");
        return res.text();
      })
      .then((csvText) => {
        const parsed = Papa.parse<string[]>(csvText, { dynamicTyping: false });
        if (parsed.errors.length > 0) {
          setError("Failed to parse CSV");
        } else {
          setData(parsed.data as string[][]);
        }
      })
      .catch((e) => {
        setError("Failed to load CSV");
        console.error(e);
      })
      .finally(() => setLoading(false));
  }, [url, visible]);

  const columnDefs: ColDef[] = useMemo(() => {
    if (!data.length) return [];
    const headerRow = data[0];
    return headerRow.map((header, colIdx) => ({
      headerName: header || `Column ${colIdx + 1}`,
      field: `col_${colIdx}`,
      resizable: true,
      sortable: true,
      filter: true,
      cellStyle: (params: CellClassParams): Record<string, string> => {
        if (selectedCell && params.node.rowIndex === selectedCell.row && colIdx === selectedCell.col) {
          return { background: "rgba(0, 0, 255, 0.1)" };
        }
        return {};
      },
    }));
  }, [data, selectedCell]);

  const rowData = useMemo(() => {
    if (!data.length) return [];
    return data.slice(1).map((rowArr, _) => {
      const rowObj: Record<string, string> = {};
      rowArr.forEach((cell, colIdx) => {
        rowObj[`col_${colIdx}`] = cell;
      });
      return rowObj;
    });
  }, [data]);

  const onCellClicked = (event: any) => {
    setSelectedCell({
      row: event.rowIndex,
      col: event.colDef.field ? parseInt(event.colDef.field.replace("col_", "")) : 0,
    });
  };

  if (!visible) return null;

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500 mx-auto mb-4"></div>
          <p>Loading CSV...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <p>Error loading CSV</p>
        </div>
      </div>
    );
  }

  if (!data.length) {
    return <div className="text-center text-gray-500">No CSV data loaded</div>;
  }

  return (
    <div className="w-full h-[calc(100vh-40px)] flex flex-col overflow-auto">
      {fileName && <div className="p-2 font-semibold">{fileName}</div>}
      <div className="flex-grow overflow-auto mt-10 p-3">
        <div className="ag-theme-balham" style={{ width: "100%", height: "100%" }}>
          <AgGridReact
            domLayout="autoHeight"
            columnDefs={columnDefs}
            rowData={rowData}
            onCellClicked={onCellClicked}
            suppressCellFocus={true}
          />
        </div>
      </div>
      <style>{`
        /* Highlighting handled by cellStyle */
      `}</style>
    </div>
  );
};

export default CSVViewer;
